import {
  AttachFileOutlined,
  ChatBubbleOutlineOutlined,
  ChevronRightRounded,
  HouseOutlined,
  InfoOutlined,
  MessageOutlined,
  PeopleOutline,
  PersonOutline,
} from "@mui/icons-material";
import { Box, Checkbox, Tooltip, Typography } from "@mui/material";
import {
  amber,
  blue,
  green,
  grey,
  orange,
  red,
  yellow,
} from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import { isFunction, isNil, min, size } from "lodash";
import { memo, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../../../components/layout/Flx";
import CommentsRenderer from "../../../../components/renderers/CommentsRenderer";
import CommentThreadPreviewTooltip from "../../../../components/tooltips/CommentThreadPreviewTooltip";
import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import { selectOptionsDocumentStatus } from "../../../../constants/selectOptions/selectOptionsDocumentStatus";
import CellRendererLoanFileStatus from "../../../../components/agGrid/cellRenderer/CellRendererLoanFileStatus";
import CellRendererUserByEmail from "../../../../components/agGrid/cellRenderer/CellRendererUserByEmail";
import { columnTypesDate } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDate";
import { columnTypesNumber } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesNumber";
import { filePreviewSetData } from "../../../../redux/reducers/filePreviewReducer";
import CellRendererLoanFileActions from "../LoanDrilldownFilesTab/LoanFilesTable/CellRendererLoanFileActions";

const LoanDocumentManagerTable = ({ rowData, onRowClicked, editing }) => {
  const dispatch = useDispatch();
  const columnDefs = useMemo(
    () => [
      {
        width: 155,
        maxWidth: 155,
        flex: 0,
        headerName: "Actions",
        resizable: false,
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          paddingLeft: "0px",
          borderLeft: "none !important",
        },
        cellRenderer: (params) => {
          if (params.node.group) {
            return params.value;
          }
          return <CellRendererLoanFileActions params={params} />;
        },
      },
      {
        headerName: "Filename",
        field: "file_display_name",
        cellEditor: "agTextCellEditor",
        editable: true,
        minWidth: 250,

        // tooltipComponent: () => <FileTooltip editing={editing} />,
        // tooltipValueGetter: (params) =>
        //   editing
        //     ? "Edit File Name"
        //     : "Left click to preview file  |  Right click for more options",
        // sort:
        //   sortFilter?.colId === "file_display_name" ? sortFilter?.sort : null,
      },
      {
        headerName: "Status",
        field: "documentStatus",
        // rowGroup: layoutMode === LOAN_FILES_TABLE_LAYOUT_TYPES.STATUS,
        // rowGroup: groupByStatus,
        // pinned: freezePanes ? "left" : null,
        // editable: editing,
        cellEditor: "agRichSelectCellEditor",
        // sort: sortFilter?.colId === "documentStatus" ? sortFilter?.sort : null,
        valueGetter: (params) => {
          const value = params.data?.documentStatus;
          return value === undefined || value === null || value === ""
            ? "Uncategorized"
            : value;
        },
        minWidth: 160,
        width: 160,
        // flex: 0,
        // Custom comparator for sorting in specific order
        comparator: (valueA, valueB) => {
          const statusOrder = [
            "New Upload",
            "Pending Review",
            "Incomplete",
            "Rejected",
            "Accepted",
            // "Initial Reviewed",
            // "Legal Reviewed",
            // "Rejected",
            // "Expired",
            // "Uncategorized", // Put uncategorized at the end
          ];

          const indexA = statusOrder.indexOf(valueA);
          const indexB = statusOrder.indexOf(valueB);

          // Handle values not in the predefined order (put them at the end)
          const finalIndexA = indexA === -1 ? statusOrder.length : indexA;
          const finalIndexB = indexB === -1 ? statusOrder.length : indexB;

          return finalIndexA - finalIndexB;
        },
        cellEditorParams: {
          values: selectOptionsDocumentStatus || [],
          allowTyping: true,
          filterList: true,
          highlightMatch: true,
          valueListMaxHeight: 220,
          valueListWidth: 620,
        },
        cellClass: "ag-cell-vertical-center",
        cellStyle: (params) => {
          const status = params?.data?.documentStatus;
          const baseStyles = {
            color: "inherit",
            background: null,
            borderLeft: null,
          };

          if (
            status === "Initial Reviewed" ||
            status === "Legal Reviewed" ||
            status === "Pending Review"
          ) {
            baseStyles.background = amber[50];
            baseStyles.borderLeft = `2px solid ${orange[100]}`;
          }
          if (
            status === "Expired" ||
            status === "Rejected" ||
            status === "Incomplete"
          ) {
            baseStyles.background = red[50];
            baseStyles.color = red[900];
            baseStyles.borderLeft = `2px solid ${red[100]}`;
          }

          if (status === "Accepted") {
            baseStyles.color = green[900];
            baseStyles.background = green[50];
            baseStyles.borderLeft = `2px solid ${green[100]}`;
          }
          return baseStyles;
        },

        autoHeight: true,
        cellRenderer: (params) => (
          <CellRendererLoanFileStatus params={params} />
        ),
      },
      {
        headerName: "Document Type",
        field: "docType",
        width: 135,
        cellClass: "ag-cell-vertical-center",

        cellStyle: (params) => {
          const status = params.value;
          return {
            cursor: "pointer",
            opacity: isNil(status) ? 0.65 : 1,
            fontStyle: isNil(status) ? "italic" : "normal",
          };
        },
        valueFormatter: (p) => {
          const status = p.value;

          if (isNil(status)) {
            return "N/A";
          }
          return status;
        },
      },

      {
        headerName: "Upload Date",
        field: "uploadDate",
        type: "dateTime",
        width: 160,
        flex: 0,
        // sort: sortFilter?.colId === "uploadDate" ? sortFilter?.sort : null,
        hide: editing,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
      },
      {
        headerName: "Uploaded By",
        field: "uploadedBy",
        width: 160,
        flex: 0,
        hide: editing,
        wrapHeaderText: true,
        autoHeaderHeight: true,
        suppressHeaderFilterButton: true,
        suppressHeaderMenuButton: true,
        // tooltipValueGetter: (params) => `Year: ${params.value}`,
        tooltipValueGetter: (params) => params.value,
        cellRenderer: CellRendererUserByEmail,
      },
      {
        width: 140,
        flex: 0,
        headerName: "Associations",
        cellClass: "ag-cell-vertical-center",
        cellStyle: {
          cursor: "pointer",
          // background: "red",
          // padding: 0,
          // display: "flex",
          // justifyContent: "space-evenly",
          // alignItems: "center",
        },

        valueGetter: (p) => {
          const documentIds = p.data?.uploadedDocumentIds || [];
          return size(documentIds);
        },
        // tooltipValueGetter: (p) => {
        //   if (size(p?.data?.uploadedDocumentIds) === 0) {
        //     return "No documents currently associated with this condition.";
        //   }
        //   if (size(p?.data?.uploadedDocumentIds) === 1) {
        //     return "(1) condition document .";
        //   }
        //   return `(${size(p?.data?.uploadedDocumentIds)}) condition documents`;
        // },
        cellRenderer: (p) => {
          return (
            <Flx
              fw
              ac
              // g={1}
              center
              sx={{
                justifyContent: "space-between",
                // justifyContent: "space-evenly",
                height: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                pr: 0,
                paddingLeft: "18px",
                paddingRight: "12px",
                // px: 1,
                flexGrow: 1,
              }}
            >
              {/* <CommentThreadPreviewTooltip
                comments={p.data?.comments || p.data?.conditionComments}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CommentsRenderer
                    onlyCount
                    comments={p.data?.comments || p.data?.conditionComments}
                  />
                </Box>
              </CommentThreadPreviewTooltip> */}
              <CommentAssociationCountBadge
                count={size(p?.data?.uploadedDocumentIds)}
              />
              <PropertyAssociationCountBadge
                count={size(p?.data?.uploadedDocumentIds)}
              />
              <BorrowerAssociationCountBadge
                count={size(p?.data?.uploadedDocumentIds)}
              />
              {/* <Tooltip title={getCommentCountTooltipText(p)}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <CommentsRenderer
                    onlyCount
                    comments={p.data?.comments || p.data?.conditionComments}
                  />
                </Box>
              </Tooltip> */}
              {/* <CommentAssociationCountBadge
                count={size(p?.data?.uploadedDocumentIds)}
              /> */}
            </Flx>
          );
        },
      },
    ],
    [rowData],
  );

  const getRowId = useCallback((params) => params.data._id, []);

  const defaultColDef = useMemo(
    () => ({
      cursor: "pointer",
      flex: 1,
      minWidth: 140,
      // tooltipComponent: () => (
      //   <Flx
      //     ac
      //     g={1}
      //     sx={{
      //       background: "#ffffff",
      //       border: `1px solid ${grey[300]}`,
      //       p: 1,
      //       borderRadius: 2,
      //     }}
      //   >
      //     Click row to address Loan Condition
      //     <ChevronRightRounded sx={{ color: grey[600] }} />
      //   </Flx>
      // ),
      // tooltipValueGetter: () => "Click to address condition",
    }),
    [],
  );

  const handleOnRowClicked = useCallback(
    (params) => {
      if (params.node.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      onRowClicked?.(params?.data);
    },
    [onRowClicked],
  );

  const onCellClicked = useCallback(
    (params) => {
      if (params?.colDef?.field === "file_display_name" && !editing) {
        dispatch(filePreviewSetData(params.data));
      }
    },
    [editing],
  );

  const columnTypes = useMemo(() => {
    return {
      ...columnTypesDate(),
      ...columnTypesNumber(),
    };
  }, []);
  return (
    <Box
      sx={{
        // maxHeight: "800px",
        overflowY: "auto",
        // border: (theme) => `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        ".ag-row-last": {
          borderBottom: "none !important",
        },
        ".ag-header-viewport": {
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <AgGridTableWrapper
        height="100%"
        // simpleTable
        compactTable
        suppressMinHeightWhenPopulated={rowData}
      >
        <AgGridReact
          rowData={rowData}
          columnDefs={columnDefs}
          columnTypes={columnTypes}
          getRowId={getRowId}
          defaultColDef={defaultColDef}
          // getRowStyle={getRowStyle}
          onCellClicked={onCellClicked}
          onRowClicked={handleOnRowClicked}
          domLayout="autoHeight"
          tooltipShowDelay={100}
          enableCellTextSelection
          //   tooltipMouseTrack
        />
      </AgGridTableWrapper>
    </Box>
  );
};

const DocumentIdsCountBadge = memo(({ count, onClick }) => {
  return (
    <Tooltip
      title={
        isFunction(onClick)
          ? `${count} ${count === 1 ? "comment" : "comments"} — click to expand`
          : undefined
      }
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.1,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        <AttachFileOutlined sx={{ fontSize: 13 }} />
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const BorrowerAssociationCountBadge = memo(({ count, onClick }) => {
  return (
    <AssociationCountBadge
      count={count}
      icon={<PersonOutline />}
      label="borrower"
      onClick={onClick}
    />
  );
});
const CommentAssociationCountBadge = memo(({ count, onClick }) => {
  return (
    <AssociationCountBadge
      count={count}
      icon={<ChatBubbleOutlineOutlined />}
      label="comment"
      onClick={onClick}
    />
  );
});
const PropertyAssociationCountBadge = memo(({ count, onClick }) => {
  return (
    <AssociationCountBadge
      count={count}
      icon={<HouseOutlined />}
      label="property"
      onClick={onClick}
    />
  );
});

const AssociationCountBadge = memo(({ count, icon, label, onClick }) => {
  return (
    <Tooltip
      title={`${count} ${label} ${count === 1 ? "association" : "associations"}`}
    >
      <Box
        onClick={onClick}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.1,
          cursor: onClick ? "pointer" : "default",
          color: count > 0 ? "primary.main" : "text.secondary",
          svg: { fontSize: 13 },
          "&:hover": onClick ? { color: "primary.dark" } : undefined,
        }}
      >
        {icon}
        <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1.5 }}>
          {count}
        </Typography>
      </Box>
    </Tooltip>
  );
});

const getCommentCountTooltipText = (p) => {
  if (size(p.data?.comments || p?.data?.conditionComments) === 0) {
    return "No comments currently associated with this condition.";
  }
  if (size(p.data?.comments || p?.data?.conditionComments) === 1) {
    return "(1) comment currently associated with this condition.";
  }
  return `(${size(p.data?.comments || p?.data?.conditionComments)}) comments currently associated with this condition. Click to view.`;
};
const getDocumentCountTooltipText = (p) => {
  if (size(p?.data?.uploadedDocumentIds) === 0) {
    return "No documents currently associated with this condition.";
  }
  if (size(p?.data?.uploadedDocumentIds) === 1) {
    return "(1) condition document .";
  }
  return `(${size(p?.data?.uploadedDocumentIds)}) condition documents`;
};
export default LoanDocumentManagerTable;
